/**
 * Accessible Color Swatch Component
 * Provides WCAG 2.1 AA compliant color preview with proper screen reader support
 */

import { useMemo } from "react";

interface AccessibleColorSwatchProps {
  color: string;
  size?: "sm" | "md" | "lg";
  showHex?: boolean;
  label?: string;
  description?: string;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

export function AccessibleColorSwatch({
  color,
  size = "md",
  showHex = true,
  label: _label,
  description,
  className = "",
  onClick,
  ariaLabel,
}: AccessibleColorSwatchProps) {
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  // Calculate contrast ratio for accessibility
  const contrastRatio = useMemo(() => {
    const rgb = hexToRgb(color);
    if (!rgb) return 0;

    const luminance = getLuminance(rgb);
    const whiteLuminance = 1;
    const blackLuminance = 0;

    const ratio1 = (whiteLuminance + 0.05) / (luminance + 0.05);
    const ratio2 = (luminance + 0.05) / (blackLuminance + 0.05);

    return Math.max(ratio1, ratio2);
  }, [color]);

  const textColor = useMemo(() => {
    return contrastRatio > 4.5 ? "text-white" : "text-gray-900";
  }, [contrastRatio]);

  const accessibilityLabel = useMemo(() => {
    if (ariaLabel) return ariaLabel;

    let labelValue = `Color ${color}`;
    if (_label) labelValue = `${_label}: ${color}`;

    const contrastLevel =
      contrastRatio >= 7 ? "AAA" : contrastRatio >= 4.5 ? "AA" : "Fail";
    labelValue += `, Contrast ratio: ${contrastRatio.toFixed(2)}:1, ${contrastLevel} compliance`;

    if (description) labelValue += `. ${description}`;

    return labelValue;
  }, [color, _label, description, contrastRatio, ariaLabel]);

  const handleClick = () => {
    onClick?.();
  };

  const swatchElement = (
    <div className={`flex items-center space-x-2 ${className}`}>
      <button
        onClick={handleClick}
        className={`${sizeClasses[size]} rounded border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all hover:border-gray-400`}
        style={{ backgroundColor: color }}
        aria-label={accessibilityLabel}
        title={accessibilityLabel}
        type="button"
      >
        <span className="sr-only">{accessibilityLabel}</span>
      </button>

      {showHex && (
        <div className="flex flex-col">
          <span
            className={`font-mono ${textSizes[size]} ${textColor} bg-gray-100 px-2 py-1 rounded`}
          >
            {color}
          </span>
          <span className="text-xs text-gray-500">
            {contrastRatio >= 7 ? "AAA" : contrastRatio >= 4.5 ? "AA" : "Poor"}
          </span>
        </div>
      )}
    </div>
  );

  return swatchElement;
}

// Helper functions for color calculations
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function getLuminance(rgb: { r: number; g: number; b: number }): number {
  const rsRGB = rgb.r / 255;
  const gsRGB = rgb.g / 255;
  const bsRGB = rgb.b / 255;

  const r =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const g =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const b =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Accessible Substrate Color Preview
 * Extends AccessibleColorSwatch for substrate-specific use cases
 */
export function AccessibleSubstrateColorPreview({
  substrate,
  className = "",
}: {
  substrate: {
    baseColor: { hex: string };
    name?: string;
    category?: string;
  };
  className?: string;
}) {
  const description = useMemo(() => {
    let desc = "";
    if (substrate.name) desc += `Base color for ${substrate.name}`;
    if (substrate.category) desc += ` (${substrate.category} substrate)`;
    return desc;
  }, [substrate.name, substrate.category]);

  return (
    <AccessibleColorSwatch
      color={substrate.baseColor.hex}
      size="md"
      label={
        substrate.name ? `${substrate.name} base color` : "Substrate base color"
      }
      description={description}
      className={className}
    />
  );
}

/**
 * Accessible Ink Level Indicator
 * Shows ink usage with proper accessibility support
 */
export function AccessibleInkLevelIndicator({
  percentage,
  limit,
  color,
  label,
}: {
  percentage: number;
  limit: number;
  color: string;
  label: string;
}) {
  const isOverLimit = percentage > limit;
  const status = isOverLimit
    ? "over limit"
    : percentage > limit * 0.9
      ? "near limit"
      : "within limits";

  return (
    <div className="flex items-center space-x-2">
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm text-gray-500">{percentage}%</span>
        </div>
        <div
          className="w-full bg-gray-200 rounded-full h-2"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-label={`${label}: ${percentage}% of ${limit}% limit, ${status}`}
        >
          <div
            className={`h-2 rounded-full transition-all ${
              isOverLimit
                ? "bg-red-500"
                : percentage > limit * 0.9
                  ? "bg-yellow-500"
                  : "bg-green-500"
            }`}
            style={{ width: `${Math.min((percentage / limit) * 100, 100)}%` }}
          />
        </div>
      </div>
      <AccessibleColorSwatch
        color={color}
        size="sm"
        showHex={false}
        ariaLabel={`${label} color preview`}
      />
    </div>
  );
}
